/**
 * UTM capture + propagation.
 *
 * On first hit of any page, scan the URL for `utm_*` + Google's
 * `gclid` / Meta's `fbclid` click identifiers, and persist them
 * to sessionStorage. Subsequent `track()` calls auto-merge these
 * fields into every event's `props` so we get full attribution
 * from landing → signup → purchase without any per-event
 * boilerplate.
 *
 * Why sessionStorage:
 *   - Lives for the whole tab lifetime (visits across pages
 *     within one session all carry the same campaign tag).
 *   - Dies when the tab closes (so a user visiting from a Twitter
 *     ad today and direct tomorrow gets two cleanly separated
 *     attribution windows).
 *   - Stealth-mode users still get UTM capture (it's not PII), but
 *     `track()` itself is a hard-noop in stealth so the data never
 *     leaves their browser.
 *
 * What we capture:
 *   utm_source, utm_medium, utm_campaign, utm_content, utm_term,
 *   gclid, fbclid, ttclid (TikTok), msclkid (Microsoft Ads).
 *
 * Each is capped at 80 chars (analytics sanitizer also caps at 200,
 * but this gives us a tighter fail-fast). Anything else from the
 * query string is ignored.
 */
import { safeSession } from "./safeStorage";

const UTM_KEY = "trubeaute.utm.v1";
const TRACKED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "ttclid",
  "msclkid",
];

/**
 * Capture any UTM / click-id params from the current URL into
 * sessionStorage. Idempotent — the FIRST landing page within a
 * session wins. Subsequent navigations within the same tab do not
 * overwrite (so a user clicking "About" then back to "/" doesn't
 * lose their original utm_source). Call this once at app boot.
 */
export const captureUtmFromUrl = () => {
  if (typeof window === "undefined") return null;
  // Already captured this tab? Don't overwrite.
  const existing = safeSession.getItem(UTM_KEY);
  if (existing) {
    try {
      return JSON.parse(existing);
    } catch {
      // fall through to re-capture
    }
  }
  const params = new URLSearchParams(window.location.search);
  const captured = {};
  for (const key of TRACKED_PARAMS) {
    const v = params.get(key);
    if (v && typeof v === "string" && v.length > 0) {
      captured[key] = v.slice(0, 80);
    }
  }
  if (Object.keys(captured).length > 0) {
    captured._captured_at = new Date().toISOString();
    captured._landing_path = (window.location.pathname || "/").slice(0, 80);
    try {
      safeSession.setItem(UTM_KEY, JSON.stringify(captured));
    } catch {
      // sessionStorage full or disabled — quietly drop
    }
    return captured;
  }
  return null;
};


// ── Acquisition bundle (sent on signup) ─────────────────────────────
// Composes UTMs + http_referrer + landing_page_url into the exact
// shape the backend's AcquisitionSource model accepts. Stored on the
// user doc once (first signup) so the founder can answer "where did
// this user come from?" without joining events → users.
const ACQ_KEY = "trubeaute.acquisition.v1";

/**
 * Capture the FIRST-landing http_referrer + landing_page_url for this
 * tab, alongside any UTM params already in sessionStorage. Idempotent —
 * a user navigating around the app doesn't overwrite their original
 * landing fingerprint.
 *
 * Call this ONCE at app boot, alongside `captureUtmFromUrl`. The pair
 * gives us the full picture: where they came from (referrer) + where
 * they landed (landing_page_url) + what campaign brought them (utm_*).
 */
export const captureLandingFingerprint = () => {
  if (typeof window === "undefined") return null;
  const existing = safeSession.getItem(ACQ_KEY);
  if (existing) {
    try {
      return JSON.parse(existing);
    } catch {
      // fall through to re-capture
    }
  }
  const ref = (typeof document !== "undefined" && document.referrer) || "";
  const fp = {
    http_referrer: ref ? ref.slice(0, 400) : null,
    landing_page_url: (
      window.location.pathname + window.location.search
    ).slice(0, 400),
    _captured_at: new Date().toISOString(),
  };
  try {
    safeSession.setItem(ACQ_KEY, JSON.stringify(fp));
  } catch {
    // sessionStorage full — silently drop
  }
  return fp;
};

/**
 * Build the {utm_*, http_referrer, landing_page_url, clarity_session_id}
 * payload to send on signup. Reads from both sessionStorage buckets
 * + `window.clarity('get', 'sessionId')` (if Clarity is loaded). All
 * fields are nullable.
 */
export const buildAcquisitionPayload = () => {
  if (typeof window === "undefined") return null;
  const out = {};
  // UTMs
  const utm = getStoredUtm() || {};
  for (const k of [
    "utm_source", "utm_medium", "utm_campaign",
    "utm_content", "utm_term",
  ]) {
    if (utm[k]) out[k] = String(utm[k]).slice(0, 120);
  }
  // Landing fingerprint
  try {
    const raw = safeSession.getItem(ACQ_KEY);
    if (raw) {
      const fp = JSON.parse(raw);
      if (fp.http_referrer) out.http_referrer = String(fp.http_referrer).slice(0, 400);
      if (fp.landing_page_url) out.landing_page_url = String(fp.landing_page_url).slice(0, 400);
    }
  } catch {
    // ignore — partial payload is fine
  }
  // Clarity session ID — only present when Clarity has loaded AND
  // the user is NOT in stealth mode.
  try {
    if (typeof window.clarity === "function") {
      // Per Clarity docs: clarity('get', 'sessionId') is sync after init
      const sid = window.clarity("get", "sessionId");
      if (sid && typeof sid === "string") {
        out.clarity_session_id = sid.slice(0, 80);
      }
    }
  } catch {
    // Clarity not initialized — skip
  }
  return Object.keys(out).length > 0 ? out : null;
};

/**
 * Read the UTM bundle for the current session, or null if no campaign
 * params were ever captured. Used by `track()` to auto-merge into
 * every event's props.
 */
export const getStoredUtm = () => {
  if (typeof window === "undefined") return null;
  const raw = safeSession.getItem(UTM_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // Strip the internal bookkeeping keys before returning to track()
    const { _captured_at, _landing_path, ...rest } = parsed;
    return Object.keys(rest).length > 0 ? rest : null;
  } catch {
    return null;
  }
};
